import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { clearSession, getUserRole } from '../services/authService';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
    Tooltip,
    Select,
    MenuItem,
    Menu,
    Chip,
    Collapse,
    Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AppsIcon from '@mui/icons-material/Apps';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import LogoutIcon from '@mui/icons-material/Logout';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BadgeIcon from '@mui/icons-material/Badge';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TableBarIcon from '@mui/icons-material/TableBar';

import AISidebar from './AISidebar';
import NotificationCenter from './notifications/NotificationCenter';
import ConnectivityIndicator from './common/ConnectivityIndicator';
import OfflineBanner from './common/OfflineBanner';
import AppDrawer from './navigation/AppDrawer';
import MobileBottomNav from './layout/MobileBottomNav';
import { useThemeMode } from '../context/ThemeContext';
import { useLocationContext } from '../context/LocationContext';

const leftDrawerWidth = 260;
const rightDrawerWidth = 350;

export default function Layout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
    const [appDrawerOpen, setAppDrawerOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState({
        foh: true,
        supplyChain: true,
        workforce: true,
        intelligence: true,
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { mode, toggleTheme } = useThemeMode();
    const { locations, currentLocationId, setCurrentLocationId, locationsLoading, locationsError } = useLocationContext();
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const toggleAiSidebar = () => setAiSidebarOpen(!aiSidebarOpen);

    // Global listener for open-app-drawer events dispatched by launchpads / shortcuts
    useEffect(() => {
        const handleOpenAppDrawer = () => setAppDrawerOpen(true);
        window.addEventListener('open-app-drawer', handleOpenAppDrawer);
        return () => window.removeEventListener('open-app-drawer', handleOpenAppDrawer);
    }, []);

    // Recalibrate responsive layout containers, data tables, and widget grids upon sidebar animation
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
        const timeout = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 320); // match transition duration
        return () => clearTimeout(timeout);
    }, [aiSidebarOpen]);

    const toggleGroup = (key) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getCurrentPageName = () => {
        const path = location.pathname;
        if (path === '/') return 'Dashboard';
        if (path === '/pos') return 'Point of Sale (POS)';
        if (path === '/kitchen-display') return 'Kitchen Display System (KDS)';
        if (path === '/inventory') return 'Inventory Master';
        if (path === '/analytics') return 'Executive Analytics';
        if (path === '/scheduling') return 'Shift Scheduling';
        if (path === '/customers') return 'Customer Loyalty & CRM';
        if (path === '/cash') return 'Cash Drawer Management';
        if (path === '/transactions') return 'Transaction History';
        if (path === '/ai-insights') return 'AI Strategic Insights';
        if (path === '/supply-chain') return 'Supply Chain & POs';
        if (path === '/invoice-upload') return 'Smart Invoice OCR';
        if (path === '/bom-builder') return 'Recipe & BOM Builder';
        if (path === '/marketplace') return 'Supplier Marketplace';
        if (path === '/variance-dashboard') return 'Inventory Variance';
        if (path === '/booking-calendar') return 'Table & Order Bookings';
        if (path === '/dynamic-chart') return 'Custom Metric Charts';
        if (path === '/employees') return 'Staff & RBAC Roster';
        if (path === '/clients') return 'Clients & Locations';
        if (path === '/timeclock') return 'Labor Compliance Time Clock';
        return 'Overview';
    };

    const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);

    const getStoredRole = () => getUserRole();

    const [userRole, setUserRole] = useState(getStoredRole);

    useEffect(() => {
        const updateRole = () => {
            setUserRole(getStoredRole());
        };
        window.addEventListener('sims-auth-changed', updateRole);
        window.addEventListener('storage', updateRole);
        return () => {
            window.removeEventListener('sims-auth-changed', updateRole);
            window.removeEventListener('storage', updateRole);
        };
    }, []);

    const navigationSections = [
        {
            key: 'foh',
            title: 'Floor Operations (FOH)',
            icon: <LocalDiningIcon fontSize="small" />,
            items: [
                { text: 'Point of Sale', icon: <PointOfSaleIcon />, path: '/pos', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'] },
                { text: 'Floor Plan & Tables', icon: <TableBarIcon />, path: '/floor-plan', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'] },
                { text: 'Kitchen Display (KDS)', icon: <LocalDiningIcon />, path: '/kitchen-display', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'] },
                { text: 'Cash Drawer', icon: <PointOfSaleIcon />, path: '/cash', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'] },
                { text: 'Time Clock', icon: <AccessTimeIcon />, path: '/timeclock', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'] },
            ]
        },
        {
            key: 'supplyChain',
            title: 'Supply Chain & Stock (BOH)',
            icon: <LocalShippingIcon fontSize="small" />,
            items: [
                { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Supply Chain & POs', icon: <ReceiptIcon />, path: '/supply-chain', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Invoice OCR', icon: <ReceiptIcon />, path: '/invoice-upload', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'BOM / Recipes', icon: <InventoryIcon />, path: '/bom-builder', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Stock Variance', icon: <AssessmentIcon />, path: '/variance-dashboard', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Marketplace', icon: <AutoAwesomeIcon />, path: '/marketplace', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
            ]
        },
        {
            key: 'workforce',
            title: 'Workforce & Guests',
            icon: <GroupIcon fontSize="small" />,
            items: [
                { text: 'Staff Roster', icon: <BadgeIcon />, path: '/employees', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Clients & Locations', icon: <ApartmentIcon />, path: '/clients', roles: ['ROLE_SUPER_ADMIN'] },
                { text: 'Shift Scheduling', icon: <CalendarMonthIcon />, path: '/scheduling', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Customer Loyalty', icon: <PeopleIcon />, path: '/customers', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Bookings', icon: <CalendarMonthIcon />, path: '/booking-calendar', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
            ]
        },
        {
            key: 'intelligence',
            title: 'Intelligence & Financials',
            icon: <BarChartIcon fontSize="small" />,
            items: [
                { text: 'Executive Analytics', icon: <AssessmentIcon />, path: '/analytics', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'AI Insights Hub', icon: <AutoAwesomeIcon />, path: '/ai-insights', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Transactions', icon: <ReceiptIcon />, path: '/transactions', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
                { text: 'Dynamic Metrics', icon: <SmartToyIcon />, path: '/dynamic-chart', roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'] },
            ]
        }
    ];

    // Trigger resize event after sidebar transition to update grid layouts
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 300);
        return () => clearTimeout(timeout);
    }, [aiSidebarOpen]);

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
            <Toolbar sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 900,
                            fontSize: '1.1rem',
                            letterSpacing: 0.5,
                        }}
                    >
                        S
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="900" color="primary" sx={{ lineHeight: 1.1, letterSpacing: 0.5 }}>
                            SIMS
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                            HOSPITALITY OS
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Account and session">
                    <Chip
                        label={userRole.replace('ROLE_', '').replace('_', ' ')}
                        size="small"
                        color={userRole === 'ROLE_SUPER_ADMIN' ? 'error' : (userRole === 'ROLE_ADMIN' ? 'primary' : (userRole === 'ROLE_MANAGER' ? 'secondary' : 'default'))}
                        onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
                        sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
                            transition: 'all 0.2s ease',
                        }}
                    />
                </Tooltip>
                <Menu
                    anchorEl={roleMenuAnchor}
                    open={Boolean(roleMenuAnchor)}
                    onClose={() => setRoleMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={() => { setRoleMenuAnchor(null); setAppDrawerOpen(true); }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <AppsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="App Drawer" secondary="All 18+ Tools" />
                    </MenuItem>
                    <MenuItem onClick={() => { setRoleMenuAnchor(null); toggleAiSidebar(); }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <SmartToyIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="AI Copilot" secondary="Strategic Assistant" />
                    </MenuItem>
                    <MenuItem onClick={() => { toggleTheme(); setRoleMenuAnchor(null); }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary={mode === 'dark' ? 'Light Daylight Mode' : 'Cyber-Neon Dark Mode'} />
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem onClick={() => { setRoleMenuAnchor(null); clearSession(); navigate('/login'); }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <LogoutIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary="Log Out" primaryTypographyProps={{ color: 'error', fontWeight: 600 }} />
                    </MenuItem>
                </Menu>
            </Toolbar>
            <Divider />

            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Active Location
                </Typography>
                <Select
                    fullWidth
                    size="small"
                    value={currentLocationId || ''}
                    onChange={(e) => setCurrentLocationId(e.target.value)}
                    displayEmpty
                    disabled={locationsLoading || locations.length === 0}
                    renderValue={(selected) => {
                        if (!selected || selected === '') {
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    {locationsLoading ? 'Loading locations…' : 'Select location'}
                                </Typography>
                            );
                        }
                        if (selected === 'ALL') {
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                                    <StorefrontIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }}>
                                        <Typography variant="body2" fontWeight={700} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.825rem' }}>
                                            All Locations
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.68rem', lineHeight: 1.1 }}>
                                            All branches
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }
                        const loc = locations.find(l => l.id === selected);
                        if (!loc) {
                            return <Typography variant="body2" color="text.secondary">Select location</Typography>;
                        }
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                                <StorefrontIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }}>
                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.825rem' }}>
                                        {loc.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.68rem', lineHeight: 1.1 }}>
                                        {loc.code}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }}
                    sx={{
                        mt: 0.5,
                        borderRadius: 2,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        '& .MuiSelect-select': {
                            py: 0.75,
                            px: 1.25,
                            pr: '32px !important',
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            overflow: 'hidden',
                        },
                        '& .MuiSelect-icon': {
                            right: 8,
                        }
                    }}
                >
                    {locations.length === 0 && (
                        <MenuItem value="" disabled>
                            {locationsLoading ? 'Loading locations…' : 'No locations configured'}
                        </MenuItem>
                    )}
                    {locations.length > 1 && (
                        <MenuItem value="ALL" sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                <StorefrontIcon fontSize="small" color={currentLocationId === 'ALL' ? 'primary' : 'action'} sx={{ flexShrink: 0 }} />
                                <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>All Locations</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        Aggregated view across all branches
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    )}
                    {locations.map((loc) => (
                        <MenuItem key={loc.id} value={loc.id} sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                                <StorefrontIcon fontSize="small" color={loc.id === currentLocationId ? 'primary' : 'action'} sx={{ flexShrink: 0 }} />
                                <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{loc.name}</Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {loc.code}{loc.clientName ? ` • ${loc.clientName}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
                {locationsError && (
                    <Typography variant="caption" color="error" sx={{ px: 1, mt: 0.5, display: 'block' }}>
                        {locationsError}
                    </Typography>
                )}
            </Box>

            <Box sx={{ px: 2, pb: 1 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AppsIcon />}
                    onClick={() => {
                        setMobileOpen(false);
                        setAppDrawerOpen(true);
                    }}
                    sx={{
                        borderRadius: 2,
                        py: 0.9,
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        letterSpacing: 0.3,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            bgcolor: 'action.hover',
                        }
                    }}
                >
                    App Drawer (All Apps)
                </Button>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* GROUPED ACCORDION / COLLAPSIBLE NAVIGATION */}
            <List sx={{ px: 1.5, flexGrow: 1, overflowY: 'auto' }} component="nav">
                {/* Primary Top-Level Dashboard Link */}
                <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                        selected={location.pathname === '/'}
                        onClick={() => navigate('/')}
                        sx={{
                            borderRadius: 2,
                            py: 0.9,
                            px: 2,
                            '&.Mui-selected': {
                                bgcolor: mode === 'dark' ? 'rgba(180, 248, 77, 0.15)' : 'primary.main',
                                color: mode === 'dark' ? 'primary.main' : '#ffffff',
                                '&:hover': {
                                    bgcolor: mode === 'dark' ? 'rgba(180, 248, 77, 0.25)' : 'primary.dark',
                                },
                                '& .MuiListItemIcon-root': {
                                    color: mode === 'dark' ? 'primary.main' : '#ffffff',
                                },
                            },
                            '&:hover': {
                                bgcolor: 'action.hover',
                            }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 34, color: location.pathname === '/' ? (mode === 'dark' ? 'primary.main' : '#fff') : 'text.secondary' }}>
                            <DashboardIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Dashboard"
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: location.pathname === '/' ? 700 : 500,
                            }}
                        />
                    </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 0.75, opacity: 0.6 }} />

                {navigationSections.map((section) => {
                    const accessibleItems = section.items.filter(item => item.roles.includes(userRole));
                    if (accessibleItems.length === 0) return null;

                    const isOpen = openGroups[section.key];

                    return (
                        <Box key={section.key} sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => toggleGroup(section.key)}
                                sx={{
                                    py: 0.75,
                                    px: 1.5,
                                    borderRadius: 1.5,
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                                    {section.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={section.title}
                                    primaryTypographyProps={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                    }}
                                />
                                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </ListItemButton>

                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding sx={{ mt: 0.5 }}>
                                    {accessibleItems.map((item) => {
                                        const isSelected = location.pathname === item.path;
                                        return (
                                            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                                                <ListItemButton
                                                    selected={isSelected}
                                                    onClick={() => navigate(item.path)}
                                                    sx={{
                                                        borderRadius: 2,
                                                        py: 0.85,
                                                        px: 2,
                                                        '&.Mui-selected': {
                                                            bgcolor: mode === 'dark' ? 'rgba(180, 248, 77, 0.15)' : 'primary.main',
                                                            color: mode === 'dark' ? 'primary.main' : '#ffffff',
                                                            '&:hover': {
                                                                bgcolor: mode === 'dark' ? 'rgba(180, 248, 77, 0.25)' : 'primary.dark',
                                                            },
                                                            '& .MuiListItemIcon-root': {
                                                                color: mode === 'dark' ? 'primary.main' : '#ffffff',
                                                            },
                                                        },
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 34, color: isSelected ? (mode === 'dark' ? 'primary.main' : '#fff') : 'text.secondary' }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.text}
                                                        primaryTypographyProps={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: isSelected ? 700 : 500,
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </Box>
                    );
                })}
            </List>

            <Divider />

            {/* LOGOUT */}
            <List sx={{ px: 2, py: 1 }}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => {
                            clearSession();
                            navigate('/login');
                        }}
                        sx={{ borderRadius: 2, '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <LogoutIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Log Out" primaryTypographyProps={{ color: 'error', fontWeight: 600, fontSize: '0.875rem' }} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh', overflow: 'hidden' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - ${leftDrawerWidth}px - ${aiSidebarOpen ? rightDrawerWidth : 0}px)` },
                    ml: { sm: `${leftDrawerWidth}px` },
                    mr: { sm: aiSidebarOpen ? `${rightDrawerWidth}px` : 0 },
                    transition: 'width 0.3s ease, margin 0.3s ease',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: { xs: 0.5, sm: 1 }, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* App Drawer Launcher Button (Clover & Square Parity) */}
                    <Tooltip title="App Drawer (All 18+ Apps & Tools) [Ctrl+K]">
                        <IconButton
                            color={appDrawerOpen ? "primary" : "inherit"}
                            onClick={() => setAppDrawerOpen(true)}
                            sx={{
                                mr: { xs: 1, sm: 2 },
                                display: { xs: 'none', sm: 'inline-flex' },
                                border: '1px solid',
                                borderColor: appDrawerOpen ? 'primary.main' : 'divider',
                                borderRadius: 2,
                                bgcolor: (theme) => appDrawerOpen
                                    ? (theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.1)')
                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.main',
                                }
                            }}
                        >
                            <AppsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    
                    <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                            {getCurrentPageName()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                            Location: <strong>{currentLocationId === 'ALL' ? 'All Locations' : (locations.find(l => l.id === currentLocationId)?.name || 'No location selected')}</strong>
                        </Typography>
                    </Box>

                    {/* Network / Offline Health Indicator */}
                    <ConnectivityIndicator sx={{ mr: { xs: 0.5, sm: 2 } }} />

                    {/* Portal target for page-specific actions like ModularPage edit controls (desktop/tablet only) */}
                    <Box id="app-bar-actions" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mr: { xs: 0, sm: 1.5 } }} />

                    {/* Notification Center */}
                    <NotificationCenter />

                    {/* User Avatar */}
                    <Tooltip title="Account menu">
                        <IconButton sx={{ ml: { xs: 0.5, sm: 1 }, mr: { xs: 0, sm: 1.5 }, p: { xs: 0.5, sm: 1 } }} onClick={(e) => setRoleMenuAnchor(e.currentTarget)}>
                            <Avatar alt="User Profile" sx={{ width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 }, bgcolor: 'primary.main', fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700 }}>
                                {userRole.charAt(5) || 'A'}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                    
                    {/* AI Copilot Sidebar Toggle */}
                    <Tooltip title={aiSidebarOpen ? "Close AI Copilot" : "Open AI Copilot"}>
                        <IconButton
                            color={aiSidebarOpen ? "primary" : "inherit"}
                            onClick={toggleAiSidebar}
                            sx={{
                                display: { xs: 'none', sm: 'inline-flex' },
                                border: '1px solid',
                                borderColor: aiSidebarOpen ? 'primary.main' : 'divider',
                                bgcolor: aiSidebarOpen ? (mode === 'dark' ? 'rgba(180, 248, 77, 0.15)' : 'rgba(37, 99, 235, 0.1)') : 'transparent',
                                mr: 1,
                            }}
                        >
                            <SmartToyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Theme Mode Toggle */}
                    <Tooltip title={mode === 'dark' ? 'Switch to Daylight Light Mode' : 'Switch to Cyber-Neon Dark Mode'}>
                        <IconButton
                            color="inherit"
                            onClick={toggleTheme}
                            sx={{
                                display: { xs: 'none', sm: 'inline-flex' },
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>
            
            {/* LEFT NAVIGATION DRAWER */}
            <Box component="nav" sx={{ width: { sm: leftDrawerWidth }, flexShrink: { sm: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: leftDrawerWidth, borderRight: '1px solid', borderColor: 'divider' },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: leftDrawerWidth, borderRight: '1px solid', borderColor: 'divider' },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* MAIN CONTENT AREA */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    p: { xs: 1.5, sm: 3 },
                    mt: { xs: '56px', sm: '64px' },
                    mb: { xs: '56px', md: 0 },
                    minHeight: { xs: 'calc(100vh - 112px)', md: 'calc(100vh - 64px)' },
                    overflowY: 'auto',
                    transition: 'margin 0.3s ease, width 0.3s ease',
                }}
            >
                <OfflineBanner />
                <Outlet />
            </Box>

            {/* RIGHT DRAWER: AI COPILOT */}
            <Drawer
                variant="persistent"
                anchor="right"
                open={aiSidebarOpen}
                sx={{
                    width: aiSidebarOpen ? rightDrawerWidth : 0,
                    flexShrink: 0,
                    transition: 'width 0.3s ease',
                    '& .MuiDrawer-paper': {
                        width: rightDrawerWidth,
                        boxSizing: 'border-box',
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? '-4px 0 20px rgba(0,0,0,0.5)' : '-4px 0 15px rgba(0,0,0,0.06)',
                    },
                }}
            >
                <AISidebar
                    currentPage={getCurrentPageName()}
                    onClose={toggleAiSidebar}
                />
            </Drawer>

            {/* CLOVER & SQUARE APP DRAWER MODAL */}
            <AppDrawer
                open={appDrawerOpen}
                onClose={() => setAppDrawerOpen(false)}
                userRole={userRole}
            />

            {/* MOBILE BOTTOM NAVIGATION BAR */}
            <MobileBottomNav
                onOpenMenu={handleDrawerToggle}
                userRole={userRole}
            />
        </Box>
    );
}
